/* eslint-disable react/display-name */
"use client";

import { Icons } from "@/components/icons";
import {
  Dialog,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import ThemeToggle from "@/components/ui/theme-toggle";
import { axiosInstance } from "@/lib/axios";
import { EventType } from "@/lib/types";
import { cn } from "@/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { isPast } from "date-fns";
import { XIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { useDebounce } from "react-use";

export function EventsSearchBar() {
  return (
    <div className="bg-[#171B20] dark:bg-white fixed inset-x-0 top-0 bg-opacity-50 dark:bg-opacity-50 backdrop-blur dark:backdrop-blur firefox:bg-opacity-90 z-10">
      <div className="max-w-[520px] mx-auto">
        <div className="flex items-center w-full py-4 pr-4 mx-auto">
          <div className="flex items-center flex-1">
            <div className="flex dark:hidden justify-start w-full lg:pl-4 pl-4">
              <Link href="/" className="inline-block ">
                <Image
                  src="/logo.png"
                  className="object-contain"
                  width={98}
                  height={44}
                  alt="Mastermind logo"
                />
              </Link>
            </div>
            <div className="hidden dark:flex justify-start w-full lg:pl-4 pl-4">
              <Link href="/" className="inline-block ">
                <Image
                  src="/logo-dark.svg"
                  className="object-contain"
                  width={98}
                  height={44}
                  alt="Mastermind logo"
                />
              </Link>
            </div>
            {/* <SearchDialog>
              <button type="button" className="shrink-0">
                <Icons.search className="w-4 h-4 text-current" />
              </button>
            </SearchDialog> */}
            <div className='shrink-0'>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SearchDialog({ children }: { children: React.ReactNode }) {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        placement="top"
        className="max-w-[520px] mx-auto w-full p-0"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Search Events</DialogTitle>
        </DialogHeader>

        <SeachResult />
      </DialogContent>
    </Dialog>
  );
}

function SeachResult() {
  const [val, setVal] = React.useState("");
  const [debouncedValue, setDebouncedValue] = React.useState("");

  const [, cancel] = useDebounce(
    () => {
      setDebouncedValue(val);
    },
    500,
    [val]
  );

  const { data, isSuccess, isFetching } = useQuery(
    ["events", "search", debouncedValue],
    async () => {
      const response = await axiosInstance.get<EventType[]>(
        "/api/events/search?" +
        new URLSearchParams({
          q: debouncedValue,
        }).toString()
      );

      return response.data;
    },
    {
      enabled: debouncedValue.trim() !== "",
    }
  );

  return (
    <div className="p-1 space-y-4">
      <div className="relative w-full rounded-full text-theme-gray bg-theme-black2 dark:bg-[#DBDFE4]">
        <Input
          className="h-10 pl-10 border-none rounded-full bg-theme-black2 dark:outline-none dark:bg-[#DBDFE4]"
          placeholder="Cari event untuk kamu ikuti disini..."
          onChange={(e) => {
            setVal(e.target.value);
          }}
          value={val}
        />

        <div className="absolute inset-y-0 left-0 flex items-center pl-4">
          {isFetching ? (
            <Icons.spinner className="w-4 h-4 text-current animate-spin" />
          ) : (
            <Icons.search className="w-4 h-4 text-current" />
          )}
        </div>
      </div>

      <div className="overflow-hidden overflow-y-auto w-full max-w-xl mx-auto rounded-[20px] p-3 max-h-[190px] bg-theme-black2 dark:bg-[#DBDFE4] text-theme-gray mt-6">
        {isSuccess
          ? data.map((event) => (
            <Link
              key={event.id}
              href={`/${event.slug}`}
              className="flex items-center p-3 focus-visible:bg-white/10 focus:outline-none"
            >
              {isPast(new Date(event.end_date)) ? (
                <Icons.clock className="w-5 h-5 mr-2 shrink-0 text-theme-accent" />
              ) : (
                <Icons.hot className="w-5 h-5 mr-2 shrink-0 text-[#C62660]" />
              )}
              <span>{event.title}</span>
            </Link>
          ))
          : null}
      </div>
    </div>
  );
}

type DialogComponentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  placement?: "top" | "bottom" | "center";
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogComponentProps
>(({ className, children, placement = "center", ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay
      className={cn("place-items-center p-4", {
        "items-end": placement === "bottom",
        "items-start": placement === "top",
        "items-center": placement === "center",
      })}
    >
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "w-full gap-4 relative px-6 py-12 text-white border-gray-800 animate-in data-[state=open]:fade-in-90 data-[state=open]:slide-in-from-bottom-10 sm:rounded-lg sm:zoom-in-90 data-[state=open]:sm:slide-in-from-bottom-0",
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <XIcon className="w-6 h-6 text-theme-gray" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogOverlay>
  </DialogPortal>
));
